/*
 * UNE PANNE N'EST PAS UNE ABSENCE DE DONNÉES (10/08/2026).
 *
 * Le motif qui a coûté trois surprises cette semaine :
 *
 *     try { … } catch (e) { setData([]); }
 *
 * L'écran affiche alors « Aucun article », « Aucune commande », ou une liste
 * déroulante muette. La propriétaire lit « il n'y a rien », alors que le vrai
 * message est « je n'ai pas pu demander ». Les deux se ressemblent à l'écran et
 * n'ont rien à voir : l'un est un fait de gestion, l'autre une panne — et une
 * panne se réessaie.
 *
 * C'est ainsi que l'assistant « Nouveau modèle » est resté inutilisable pendant
 * un jour : la route des pointures n'existait pas, l'erreur était avalée, et le
 * champ ne proposait simplement rien.
 *
 * Ce fichier ne contient QUE la traduction d'une erreur en phrase lisible.
 * L'affichage reste `NdoeError`, qui existe déjà et porte son bouton
 * « Réessayer ».
 */

/**
 * Transforme une erreur (axios ou autre) en une phrase que l'on peut montrer.
 *
 * @param {Error} e      l'erreur attrapée
 * @param {string} quoi  ce qu'on essayait de charger, à la forme « les pointures »
 * @returns {string}
 */
export function messageErreur(e, quoi = 'les données') {
  const statut = e && e.response && e.response.status;

  // Le serveur a expliqué son refus : c'est toujours le meilleur message.
  const duServeur = e && e.response && e.response.data
    && (e.response.data.error || e.response.data.message);
  if (duServeur && typeof duServeur === 'string') return duServeur;

  // Pas de réponse du tout : la requête n'est jamais arrivée, ou n'est jamais
  // revenue. C'est le cas le plus fréquent dans un dépôt à Kinshasa, et celui
  // qu'il faut nommer clairement plutôt que d'afficher une liste vide.
  if (e && e.request && !e.response) {
    return `Impossible de charger ${quoi} : le serveur n’a pas répondu. Vérifiez votre connexion, puis réessayez.`;
  }

  if (statut === 401) return 'Votre session a expiré. Reconnectez-vous.';
  if (statut === 403) return `Vous n’avez pas le droit de consulter ${quoi}.`;
  if (statut === 404) return `${quoi.charAt(0).toUpperCase()}${quoi.slice(1)} sont introuvables (adresse inconnue du serveur).`;
  if (statut >= 500) return `Le serveur n’a pas pu fournir ${quoi}. Réessayez dans un instant.`;

  return `Impossible de charger ${quoi}.`;
}

/**
 * Charge une liste SANS confondre « vide » et « en panne ».
 *
 * Rend `{ liste, erreur }` : la liste est toujours un tableau (l'écran ne casse
 * jamais), et `erreur` est renseignée UNIQUEMENT en cas de panne. Un serveur qui
 * répond « rien » rend donc `{ liste: [], erreur: null }` — et c'est bien une
 * absence de données, pas un incident.
 *
 * @param {Promise} promesse  l'appel (axios.get(...))
 * @param {string} quoi       ce qu'on charge, pour le message
 */
export async function chargerListe(promesse, quoi = 'les données') {
  try {
    const { data } = await promesse;
    return { liste: Array.isArray(data) ? data : [], erreur: null };
  } catch (e) {
    return { liste: [], erreur: messageErreur(e, quoi) };
  }
}

/**
 * Charge PLUSIEURS listes obligatoires d'un coup.
 *
 * Un écran de saisie a souvent besoin de six ou sept référentiels : si l'un
 * manque, le formulaire est inutilisable même si les autres sont là. On rend
 * donc UNE erreur dès qu'un seul manque, en NOMMANT ceux qui manquent — « les
 * pointures » est une information, « une erreur est survenue » n'en est pas une.
 *
 * @param {Array<{cle: string, quoi: string, promesse: Promise}>} appels
 * @returns {{listes: object, erreur: string|null, manquants: string[]}}
 */
export async function chargerReferentiels(appels) {
  const resultats = await Promise.all(
    appels.map(async (a) => ({ ...a, ...(await chargerListe(a.promesse, a.quoi)) })),
  );

  const listes = {};
  resultats.forEach((r) => { listes[r.cle] = r.liste; });

  const manquants = resultats.filter((r) => r.erreur).map((r) => r.quoi);
  if (!manquants.length) return { listes, erreur: null, manquants: [] };

  // Si TOUT est tombé, c'est une panne générale : inutile d'énumérer.
  const erreur = manquants.length === resultats.length
    ? 'Impossible de charger les listes de référence : le serveur n’a pas répondu. Vérifiez votre connexion, puis réessayez.'
    : `Impossible de charger ${manquants.join(', ')}. Réessayez — sans ${manquants.length > 1 ? 'ces listes' : 'cette liste'}, la saisie serait incomplète.`;

  return { listes, erreur, manquants };
}
